import { Box, Stack, styled } from '@mui/material'
import { BuildingCircleIcon, EinwertLogoIconSmall, Select, tokens } from 'ComponentLibrary'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { NavLink } from 'ComponentLibrary/src/piazza/text/Link/Link'
import { AvatarNavigationItem, NavigationItem, NavigationItemProps } from './NavigationItem'
import { FeedbackTool, FeedbackToolProps } from 'ComponentLibrary/src/molecules/FeedbackTool/FeedbackTool'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { SignOutIcon } from '@phosphor-icons/react'
import { PathPattern } from 'react-router-dom'
import { IconButton } from 'ComponentLibrary/src/piazza'

export const TOOLBAR_HEIGHT = '56px'
const NAV_ITEM_SIZE_BREAKPOINT = 1448

export type NavigationMenuItem = {
    disabled?: boolean
    Icon: NavigationItemProps['icon']
    label: string
    path: string
    pathPatterns?: PathPattern['path'][]
    tooltipLabel?: string
}
export type NavigationMenuItemWithImage = NavigationMenuItem & {
    imageUrl?: string
    imageThumbnailUrl?: string
    isPlatformUser?: boolean
    isPending?: boolean
    overlayIcon?: ReactNode
}

type AppBarProps = {
    currentPath: string
    navigationMenuItems: NavigationMenuItem[]
    inboxMenuItem?: NavigationMenuItem
    userMenuItem: NavigationMenuItemWithImage
    companyMenuItem?: NavigationMenuItemWithImage
    playgroundMenuItem?: NavigationMenuItem
    feedbackButton?: FeedbackToolProps
    customerSelection?: {
        selectedCustomer?: string
        customers: { icon?: ReactNode; label: string; value: string }[]
        setSelectedCustomer: (customerId: string) => void
    }
    logout?: () => void
    logoutLabel?: string
}

export function AppBar({
    navigationMenuItems,
    currentPath,
    customerSelection,
    inboxMenuItem,
    userMenuItem,
    companyMenuItem,
    playgroundMenuItem,
    feedbackButton,
    logout,
    logoutLabel,
}: AppBarProps) {
    const [navigationItemSize, setNavigationItemSize] = useState<'small' | 'medium'>('small')
    const headerRef = useRef<HTMLDivElement>(null)
    const selectedCustomer = customerSelection?.customers.find((customer) => customer.value === customerSelection.selectedCustomer)

    useEffect(() => {
        const handleResize = () => {
            if (headerRef.current) {
                const width = headerRef.current.clientWidth
                setNavigationItemSize(width > NAV_ITEM_SIZE_BREAKPOINT ? 'medium' : 'small')
            }
        }

        const resizeObserver = new ResizeObserver(handleResize)

        if (headerRef.current) {
            resizeObserver.observe(headerRef.current)
        }

        return () => resizeObserver.disconnect()
    }, [])

    return (
        <AppBarContainer ref={headerRef}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" gap={tokens.SizingSpacing400}>
                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing400}>
                    <NavLink
                        href={location.origin}
                        startIcon={<EinwertLogoIconSmall height={tokens.SizingIconM} width="42px" />}
                        forceReload
                    />
                    {navigationMenuItems.map(({ Icon, label, tooltipLabel, path, pathPatterns }) => (
                        <NavigationItem
                            key={path}
                            label={label}
                            tooltipLabel={tooltipLabel}
                            icon={Icon}
                            path={path}
                            currentPath={currentPath}
                            size={navigationItemSize}
                            pathPatterns={pathPatterns}
                        />
                    ))}
                </Stack>
                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing300}>
                    {feedbackButton && <FeedbackTool {...feedbackButton} size={navigationItemSize} />}
                    {inboxMenuItem && (
                        <NavigationItem
                            label={inboxMenuItem.label}
                            icon={inboxMenuItem.Icon}
                            path={inboxMenuItem.path}
                            currentPath={currentPath}
                            size={navigationItemSize}
                            pathPatterns={inboxMenuItem.pathPatterns}
                        />
                    )}
                    {playgroundMenuItem && (
                        <NavigationItem
                            label={playgroundMenuItem.label}
                            tooltipLabel={playgroundMenuItem.tooltipLabel}
                            icon={playgroundMenuItem.Icon}
                            path={playgroundMenuItem.path}
                            currentPath={currentPath}
                            size={navigationItemSize}
                            pathPatterns={playgroundMenuItem.pathPatterns}
                        />
                    )}
                    {!userMenuItem.isPending && (
                        <AvatarNavigationItem
                            label={userMenuItem.label}
                            fallbackLabel={userMenuItem.label}
                            icon={userMenuItem.Icon}
                            path={userMenuItem.path}
                            imageUrl={userMenuItem.imageUrl}
                            imageThumbnailUrl={userMenuItem.imageThumbnailUrl}
                            currentPath={currentPath}
                            size={navigationItemSize}
                            pathPatterns={userMenuItem.pathPatterns}
                            isPlatformUser={userMenuItem.isPlatformUser}
                            overlayIcon={userMenuItem.overlayIcon}
                        />
                    )}
                    {customerSelection && (
                        <Select
                            selectProps={{
                                sx: {
                                    width: '240px',
                                    height: '40px',
                                    bgcolor: tokens.ColorSurfaceInverseDefault,
                                    '.MuiOutlinedInput-notchedOutline': {
                                        border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                                    },
                                    '.MuiSelect-icon': {
                                        right: tokens.SizingSpacing150,
                                    },
                                    '.MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '40px',
                                        boxSizing: 'border-box',
                                        padding: `0 ${tokens.SizingSpacing600} 0 ${tokens.SizingSpacing150}`,
                                    },
                                },
                                renderValue: () =>
                                    selectedCustomer ? (
                                        <AppBarCustomerSelectionLabel label={selectedCustomer.label} icon={selectedCustomer.icon} />
                                    ) : undefined,
                            }}
                            value={customerSelection.selectedCustomer || ''}
                            options={customerSelection.customers}
                            renderOption={(option) => {
                                const customer = customerSelection.customers.find((candidate) => candidate.value === option.value)

                                return (
                                    <AppBarCustomerSelectionLabel
                                        label={typeof option.label === 'string' ? option.label : ''}
                                        fallbackLabel={option.label}
                                        icon={customer?.icon}
                                        fullWidth
                                    />
                                )
                            }}
                            onChange={(event) => customerSelection.setSelectedCustomer(event.target.value)}
                            size="medium"
                        />
                    )}
                    {companyMenuItem && (
                        <AvatarNavigationItem
                            label={companyMenuItem.label}
                            icon={companyMenuItem.Icon}
                            path={companyMenuItem.path}
                            imageUrl={companyMenuItem.imageUrl}
                            currentPath={currentPath}
                            size={navigationItemSize}
                            pathPatterns={companyMenuItem.pathPatterns}
                            overlayIcon={companyMenuItem.overlayIcon}
                        />
                    )}
                    {logout && (
                        <IconButton
                            icon={<SignOutIcon />}
                            tooltip={logoutLabel}
                            ariaLabel={logoutLabel}
                            onClick={logout}
                            size="small"
                            variant="tertiary"
                        />
                    )}
                </Stack>
            </Stack>
        </AppBarContainer>
    )
}

function AppBarCustomerSelectionLabel({
    label,
    fallbackLabel,
    icon,
    fullWidth,
}: {
    label: string
    fallbackLabel?: ReactNode
    icon?: ReactNode
    fullWidth?: boolean
}) {
    return (
        <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center" minWidth={0} width={fullWidth ? '100%' : undefined}>
            <Box
                component="span"
                sx={{
                    color: tokens.ColorTextLinkDefault,
                    display: 'inline-flex',
                    flexShrink: 0,
                    height: tokens.SizingIconL,
                    width: tokens.SizingIconL,
                    '& > *': {
                        height: '100%',
                        width: '100%',
                    },
                    '& img': { objectFit: 'contain' },
                }}
            >
                {icon ?? <BuildingCircleIcon />}
            </Box>
            {label ? (
                <TextWithEllipsis
                    typographyProps={{ variant: 'link', color: tokens.ColorTextLinkDefault }}
                    tooltipText={label}
                    placement="left-start"
                    fullWidth
                    tooltipProps={{
                        slotProps: {
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: { offset: [-3, 38] },
                                    },
                                ],
                            },
                        },
                    }}
                >
                    {label}
                </TextWithEllipsis>
            ) : (
                fallbackLabel
            )}
        </Stack>
    )
}

const AppBarContainer = styled(Box)(() => ({
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: tokens.ColorSurfaceDefault,
    height: TOOLBAR_HEIGHT,
    padding: `0 ${tokens.SizingSpacing300}`,
    borderBottom: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
}))
